// ============================================================
// traitCleanup - what switching a trait ON strips from a target
// ============================================================
// A trait that grants an immunity removes the conditions it is immune to
// the moment it is switched on, and says so. Shared by every editor that
// can switch a trait on (both panels), so neither forgets. Pure: each
// function returns a NEW target.

import type { Target } from '../types';
import { computeTraitCleanup } from './conditionEligibility';

/** "Pinned, Hallucinating removed by From Beyond" - what a trait just stripped. */
export interface CondFlash {
  traitLabel: string;
  removed: string[];
}

/** A target after an edit, and the flash to show when the edit stripped conditions. */
export interface CleanedTarget {
  target: Target;
  flash: CondFlash | null;
}

/** The flash for a cleanup, or null when it stripped nothing. */
export function flashOf(result: { traitLabel: string; removed: string[] }): CondFlash | null {
  return result.removed.length > 0 ? { traitLabel: result.traitLabel, removed: result.removed } : null;
}

// The boolean traits that grant an immunity when switched on.
const IMMUNITY_TRAITS = ['fromBeyond', 'blind', 'stuffOfNightmares', 'astartes', 'hardy', 'undying'] as const;

/** Was this trait off before the edit and on after it? Machine counts from 0 to a rating. */
function switchedOn(prev: Target, next: Target, field: string): boolean {
  const get = (t: Target) => (t as unknown as Record<string, unknown>)[field];
  if (field === 'machineAP') return !((get(prev) as number) > 0) && ((get(next) as number) || 0) > 0;
  return !get(prev) && !!get(next);
}

/**
 * An editor's new target, with the conditions any trait it just switched
 * on is immune to removed. When several traits strip something, the flash
 * names the last of them.
 */
export function cleanedAfterTraitEdit(prev: Target, next: Target): CleanedTarget {
  let target = next;
  let flash: CondFlash | null = null;
  for (const field of [...IMMUNITY_TRAITS, 'machineAP']) {
    if (!switchedOn(prev, target, field)) continue;
    const result = computeTraitCleanup(target.conditions, field, true);
    if (result.removed.length === 0) continue;
    target = { ...target, conditions: result.cleaned };
    flash = flashOf(result);
  }
  return { target, flash };
}
